import React, { useState, useEffect, Suspense, lazy, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import { collection, doc, runTransaction, serverTimestamp } from "firebase/firestore"; // Importações necessárias
import "./Checkout.css";
import toast from "react-hot-toast";

// Lazy load do componente Payment - movido para dentro do componente
let Payment = null;

export function Checkout({ cartItems, onClearCart, products }) {
  const { user, userData } = useAuth(); // Pegando userData para o nome do cliente
  const navigate = useNavigate();

  // Estados de Controle
  const [showPayment, setShowPayment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [showPixModal, setShowPixModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const [formData, setFormData] = useState({
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    cidade: "",
    estado: "RS",
    telefone: "",
  });

  const formatCep = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    return digits.replace(/^(\d{5})(\d{1,3})?$/, (_, part1, part2) =>
      part2 ? `${part1}-${part2}` : part1,
    );
  };

  const formatTelefone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    return digits.replace(/^(\d{2})(\d{4,5})(\d{0,4})?$/, (_, d1, d2, d3) =>
      d3 ? `(${d1}) ${d2}-${d3}` : d2 ? `(${d1}) ${d2}` : d1 ? `(${d1}` : "",
    );
  };

  const normalizeText = (value) =>
    value
      .trim()
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Z0-9 ]/g, "");

  const validateFormData = async () => {
    const cepDigits = formData.cep.replace(/\D/g, "");
    const telefoneDigits = formData.telefone.replace(/\D/g, "");

    if (cepDigits.length !== 8) {
      toast.error("Digite um CEP válido com 8 dígitos.");
      return false;
    }

    if (telefoneDigits.length < 10 || telefoneDigits.length > 11) {
      toast.error("Digite um telefone válido com DDD e 10 ou 11 dígitos.");
      return false;
    }

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cepDigits}/json/`,
      );
      if (!response.ok) {
        toast.error(
          "Não foi possível consultar o CEP. Tente novamente mais tarde.",
        );
        return false;
      }

      const cepData = await response.json();
      if (cepData.erro) {
        toast.error("CEP não encontrado. Verifique o valor e tente novamente.");
        return false;
      }

      const cepCity = normalizeText(cepData.localidade || "");
      const enteredCity = normalizeText(formData.cidade || "");

      if (cepCity !== enteredCity) {
        toast.error(
          `O CEP informado corresponde a ${cepData.localidade}, mas você escreveu ${formData.cidade}. Verifique a cidade e tente novamente.`,
        );
        return false;
      }
    } catch (error) {
      console.error("Erro ao validar CEP:", error);
      toast.error(
        "Não foi possível verificar o CEP. Tente novamente mais tarde.",
      );
      return false;
    }

    return true;
  };

  // Lazy load do Payment quando necessário
  useEffect(() => {
    if (showPayment && !Payment) {
      setPaymentLoading(true);
      setPaymentError(false);
      Payment = lazy(() =>
        import("@mercadopago/sdk-react")
          .then((module) => ({
            default: module.Payment,
          }))
          .catch(() => {
            setPaymentError(true);
            setPaymentLoading(false);
            throw new Error("Falha ao carregar MercadoPago");
          }),
      );
    }
  }, [showPayment]);

  // Recalcula e valida preços e estoques diretamente contra o banco de dados
  const verifiedCart = useMemo(() => {
    return cartItems.map((item) => {
      const dbProduct = products?.find((p) => p.id === item.id);
      const realUnitPrice = dbProduct
        ? Number(dbProduct.preco ?? 0)
        : Number(item.preco ?? 0);
      const realStock = dbProduct ? Number(dbProduct.estoque ?? 0) : 0;
      return {
        ...item,
        nome: dbProduct?.nome || item.nome,
        preco: realUnitPrice,
        estoque: realStock,
      };
    });
  }, [cartItems, products]);

  const total = useMemo(() => {
    return verifiedCart.reduce(
      (acc, item) => acc + item.preco * item.quantidade,
      0,
    );
  }, [verifiedCart]);

  // Configuração memoizada do componente Payment
  const paymentConfig = useMemo(
    () => ({
      initialization: {
        amount: total,
        payer: { email: user?.email },
      },
      customization: {
        paymentMethods: {
          bankTransfer: ["pix"],
          creditCard: "all",
          installments: 12,
        },
        visual: {
          style: {
            theme: "dark",
            customVariables: {
              borderRadiusSmall: "0px",
              formBackgroundColor: "#0a0a0a",
              baseColor: "#ffffff",
            },
          },
        },
      },
    }),
    [total, user?.email],
  );

  // Scroll para cima quando os modais forem abertos
  useEffect(() => {
    if (showProcessingModal || showPixModal) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showProcessingModal, showPixModal]);

  const validateCartStock = () => {
    for (const item of verifiedCart) {
      const product = products?.find((p) => p.id === item.id);
      const estoque = Number(product?.estoque ?? item.estoque);

      if (!product) {
        toast.error(`O produto "${item.nome}" não está mais disponível.`);
        return false;
      }

      if (!Number.isFinite(estoque) || estoque <= 0) {
        toast.error(`O produto "${item.nome}" está sem estoque.`);
        return false;
      }

      if (item.quantidade > estoque) {
        toast.error(
          `A quantidade selecionada para "${item.nome}" não está disponível. Estoque atual: ${estoque}.`,
        );
        return false;
      }
    }

    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "cep") {
      setFormData({ ...formData, cep: formatCep(value) });
      return;
    }

    if (name === "telefone") {
      setFormData({ ...formData, telefone: formatTelefone(value) });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleGoToPayment = async (e) => {
    e.preventDefault();
    if (!(await validateFormData())) return;
    if (!validateCartStock()) return;
    setShowReview(true);
    setShowPayment(false);
  };

  const handleConfirmDeliveryInfo = () => {
    setShowPayment(true);
    setShowReview(false);
  };

  // Função para salvar o pedido e dar baixa de estoque atomicamente via transação no Firestore
  const salvarPedidoEAtualizarEstoque = async (mercadoPagoResult) => {
    try {
      const pedidoRef = doc(collection(db, "pedidos"));

      await runTransaction(db, async (transaction) => {
        // 1. Ler todos os produtos no banco para validar o estoque.
        // A baixa do estoque é feita pela API (webhook) quando o pagamento é aprovado,
        // pois o cliente não tem permissão de escrita na coleção "produtos".
        for (const item of verifiedCart) {
          const productRef = doc(db, "produtos", item.id);
          const productSnap = await transaction.get(productRef);

          if (!productSnap.exists()) {
            throw new Error(
              `O produto "${item.nome}" não está mais disponível no catálogo.`,
            );
          }

          const produtoDb = productSnap.data();
          const estoqueAtual = Number(produtoDb.estoque ?? 0);

          if (estoqueAtual < item.quantidade) {
            throw new Error(
              `Estoque insuficiente para "${item.nome}". Estoque disponível: ${estoqueAtual}, Solicitado: ${item.quantidade}.`,
            );
          }
        }

        // 2. Registrar o pedido no Firestore com dados e preços verificados
        const pedidoData = {
          userId: user.uid,
          userName: userData?.nome || "Cliente",
          email: user.email,
          itens: verifiedCart.map((item) => ({
            id: item.id,
            title: item.nome,
            quantity: item.quantidade,
            unit_price: item.preco,
            foto: item.fotos ? item.fotos[0] : null,
          })),
          total: total,
          status: mercadoPagoResult.status, // approved, pending, etc
          paymentMethod: mercadoPagoResult.payment_method_id,
          paymentId: mercadoPagoResult.id,
          endereco: formData,
          dataCriacao: serverTimestamp(),
          pixInfo:
            mercadoPagoResult.payment_method_id === "pix"
              ? {
                  qrCode:
                    mercadoPagoResult.point_of_interaction?.transaction_data
                      ?.qr_code,
                  qrCodeBase64:
                    mercadoPagoResult.point_of_interaction?.transaction_data
                      ?.qr_code_base64,
                }
              : null,
        };

        transaction.set(pedidoRef, pedidoData);
      });

      console.log(">>> Pedido registrado e estoque baixado com sucesso!");
      return { success: true };
    } catch (error) {
      console.error("Erro na transação de pedido e estoque:", error);
      toast.error(error.message || "Erro ao registrar pedido e atualizar estoque.");
      return { success: false, error };
    }
  };

  const processPayment = async (cardFormData) => {
    if (!validateCartStock()) return;
    setIsProcessing(true);
    setShowProcessingModal(true); // Abre o modal de processamento
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL || "https://repique-api.onrender.com";

      const response = await fetch(
        `${apiUrl}/processar-pagamento`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...cardFormData,
            transaction_amount: total,
            shipping_address: formData,
            items: verifiedCart.map((item) => ({
              id: item.id,
              title: item.nome,
              unit_price: item.preco,
              quantity: item.quantidade,
            })),
          }),
        },
      );

      const result = await response.json();

      // --- SUCESSO (PIX OU CARTÃO) ---
      if (
        result.status === "approved" ||
        (result.status === "pending" && result.payment_method_id === "pix")
      ) {
        // 1. Salva o pedido no banco e baixa o estoque atomicamente
        const transacao = await salvarPedidoEAtualizarEstoque(result);
        if (!transacao.success) {
          return;
        }

        // 2. Limpa o carrinho globalmente
        if (onClearCart) onClearCart();

        // 3. Trata a interface baseado no método
        if (result.payment_method_id === "pix") {
          setPixData({
            qrCode:
              result.point_of_interaction?.transaction_data?.qr_code,
            qrCodeBase64:
              result.point_of_interaction?.transaction_data?.qr_code_base64,
          });
          setShowPixModal(true); // Abre o modal do PIX
        } else {
          toast.success("Pagamento aprovado! Produto garantido.");
          navigate("/meu-perfil"); // Direciona para o novo dashboard do cliente
        }
      } else {
        toast.error(
          "Falha no processamento. Verifique os dados ou tente outro método.",
        );
      }
    } catch (error) {
      console.error("Erro no checkout:", error);
      toast.error("Erro ao conectar com o servidor. Tente novamente.");
    } finally {
      setIsProcessing(false);
      setShowProcessingModal(false); // Fecha o modal de processamento
    }
  };

  return (
    <div className="checkout-screen">
      <div className="checkout-container">
        {/* Coluna 1: Entrega e Pagamento */}
        <div className="checkout-section">
          <h2 className="section-title">Entrega</h2>
          <form className="checkout-form" onSubmit={handleGoToPayment}>
            <div className="input-row">
              <div className="input-group">
                <label>CEP</label>
                <input
                  type="text"
                  name="cep"
                  placeholder="00000-000"
                  value={formData.cep}
                  onChange={handleInputChange}
                  pattern="\d{5}-?\d{3}"
                  title="Digite um CEP válido: 00000-000"
                  inputMode="numeric"
                  required
                  disabled={showPayment}
                />
              </div>
              <div className="input-group">
                <label>TELEFONE</label>
                <input
                  type="tel"
                  name="telefone"
                  placeholder="(00) 00000-0000"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  pattern="\(\d{2}\) \d{4,5}-\d{4}"
                  title="Digite um telefone válido com DDD"
                  inputMode="tel"
                  required
                  disabled={showPayment}
                />
              </div>
            </div>
            <div className="input-group">
              <label>ENDEREÇO</label>
              <input
                name="endereco"
                placeholder="Rua, Avenida..."
                value={formData.endereco}
                onChange={handleInputChange}
                required
                disabled={showPayment}
              />
            </div>
            <div className="input-row">
              <div className="input-group">
                <label>NÚMERO</label>
                <input
                  type="number"
                  name="numero"
                  placeholder="123"
                  value={formData.numero}
                  onChange={handleInputChange}
                  required
                  disabled={showPayment}
                />
              </div>
              <div className="input-group">
                <label>COMPLEMENTO</label>
                <input
                  type="text"
                  name="complemento"
                  placeholder="Apto, Bloco, Andar..."
                  value={formData.complemento}
                  onChange={handleInputChange}
                  disabled={showPayment}
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input-group">
                <label>CIDADE</label>
                <input
                  name="cidade"
                  placeholder="Cidade"
                  value={formData.cidade}
                  onChange={handleInputChange}
                  required
                  disabled={showPayment}
                />
              </div>
              <div className="input-group">
                <label>ESTADO</label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                  required
                  disabled={showPayment}
                >
                  <option value="RS">RS</option>
                  <option value="AC">AC</option>
                  <option value="AL">AL</option>
                  <option value="AP">AP</option>
                  <option value="AM">AM</option>
                  <option value="BA">BA</option>
                  <option value="CE">CE</option>
                  <option value="DF">DF</option>
                  <option value="ES">ES</option>
                  <option value="GO">GO</option>
                  <option value="MA">MA</option>
                  <option value="MT">MT</option>
                  <option value="MS">MS</option>
                  <option value="MG">MG</option>
                  <option value="PA">PA</option>
                  <option value="PB">PB</option>
                  <option value="PR">PR</option>
                  <option value="PE">PE</option>
                  <option value="PI">PI</option>
                  <option value="RJ">RJ</option>
                  <option value="RN">RN</option>
                  <option value="RO">RO</option>
                  <option value="RR">RR</option>
                  <option value="SC">SC</option>
                  <option value="SP">SP</option>
                  <option value="SE">SE</option>
                  <option value="TO">TO</option>
                </select>
              </div>
            </div>

            {!showPayment && !showReview && (
              <button type="submit" className="btn-prosseguir">
                IR PARA PAGAMENTO
              </button>
            )}
          </form>

          {showReview && !showPayment && (
            <div className="delivery-review-container">
              <h2 className="section-title">Confira seus dados de entrega</h2>
              <div className="delivery-review-row">
                <div className="delivery-review-item">
                  <label>CEP</label>
                  <span>{formData.cep}</span>
                </div>
                <div className="delivery-review-item">
                  <label>TELEFONE</label>
                  <span>{formData.telefone}</span>
                </div>
              </div>
              <div className="delivery-review-row">
                <div className="delivery-review-item">
                  <label>ENDEREÇO</label>
                  <span>{formData.endereco}</span>
                </div>
                <div className="delivery-review-item">
                  <label>COMPLEMENTO</label>
                  <span>{formData.complemento || "—"}</span>
                </div>
              </div>
              <div className="delivery-review-row">
                <div className="delivery-review-item">
                  <label>CIDADE</label>
                  <span>{formData.cidade}</span>
                </div>
                <div className="delivery-review-item">
                  <label>ESTADO</label>
                  <span>{formData.estado}</span>
                </div>
              </div>
              <div className="delivery-review-actions">
                <button
                  type="button"
                  className="btn-prosseguir"
                  onClick={handleConfirmDeliveryInfo}
                >
                  CONTINUAR PARA PAGAMENTO
                </button>
                <button
                  type="button"
                  className="btn-voltar-entrega"
                  onClick={() => setShowReview(false)}
                >
                  EDITAR DADOS
                </button>
              </div>
            </div>
          )}

          {showPayment && !pixData && (
            <div className="payment-method-container">
              <h2 className="section-title">Pagamento</h2>

              {paymentLoading && (
                <div className="payment-loading-container">
                  <div className="payment-loading-spinner"></div>
                  <p className="payment-loading-text">
                    Carregando formas de pagamento...
                  </p>
                </div>
              )}

              <Suspense
                fallback={
                  <div className="payment-loading-container">
                    <div className="payment-loading-spinner"></div>
                    <p className="payment-loading-text">
                      Carregando formas de pagamento...
                    </p>
                  </div>
                }
              >
                {paymentError ? (
                  <div className="payment-error-container">
                    <p className="payment-error-text">
                      Erro ao carregar formas de pagamento.
                    </p>
                    <button
                      className="btn-retry-payment"
                      onClick={() => {
                        Payment = null;
                        setPaymentError(false);
                        setShowPayment(false);
                        setTimeout(() => setShowPayment(true), 100);
                      }}
                    >
                      TENTAR NOVAMENTE
                    </button>
                  </div>
                ) : Payment ? (
                  <Payment
                    {...paymentConfig}
                    onSubmit={processPayment}
                    onReady={() => setPaymentLoading(false)}
                    onError={() => {
                      setPaymentError(true);
                      setPaymentLoading(false);
                      toast.error(
                        "Erro ao carregar formas de pagamento. Tente recarregar a página.",
                      );
                    }}
                  />
                ) : null}
              </Suspense>

              <button
                className="btn-voltar-entrega"
                onClick={() => setShowPayment(false)}
                disabled={isProcessing}
              >
                ← Editar dados de entrega
              </button>
            </div>
          )}
        </div>

        {/* Coluna 2: Resumo Lateral */}
        <div className="checkout-summary">
          <h2 className="section-title">Resumo do Pedido</h2>
          <div className="summary-items">
            {verifiedCart.map((item) => (
              <Link key={item.id} to={`/produto/${item.id}`}>
                <div className="summary-item-row">
                  <div className="summary-item-info">
                    <div className="summary-img-container">
                      {item.fotos && item.fotos.length > 0 ? (
                        <img
                          src={item.fotos[0]}
                          alt={item.nome}
                          className="summary-prod-img"
                        />
                      ) : (
                        <div className="summary-img-placeholder">◈</div>
                      )}
                    </div>
                    <div className="summary-text-details">
                      <span className="summary-item-name">{item.nome}</span>
                      <span className="summary-item-qtd">
                        Qtd: {item.quantidade}
                      </span>
                    </div>
                  </div>
                  <span className="summary-item-price">
                    {(item.preco * item.quantidade).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="summary-total">
            <span>TOTAL</span>
            <span>
              {total.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Modal de Processamento */}
      {showProcessingModal && (
        <div className="processing-modal-overlay">
          <div className="processing-modal-content">
            <div className="processing-modal-body">
              <h3>Processando sua transação...</h3>
              <div className="loading-spinner"></div>
              <p className="processing-text">
                Aguarde enquanto processamos seu pagamento
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal do PIX */}
      {showPixModal && pixData && (
        <div className="pix-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="pix-modal-content">
            <button
              className="pix-modal-close"
              onClick={() => {
                if (
                  window.confirm("Tem certeza que deseja cancelar o pagamento?")
                ) {
                  setShowPixModal(false);
                  setPixData(null);
                  setShowPayment(false);
                }
              }}
            >
              ×
            </button>
            <div className="pix-modal-body">
              <h2 className="section-title">Pagamento via Pix</h2>
              <p>
                Tchê, falta pouco! Escaneie o código abaixo: (Este código expira
                em breve!)
              </p>

              <div className="pix-qr-wrapper">
                <img
                  src={`data:image/jpeg;base64,${pixData.qrCodeBase64}`}
                  alt="QR Code Pix"
                  className="pix-qr-img"
                />
              </div>

              <div className="pix-copy-section">
                <label>Código Copia e Cola:</label>
                <textarea readOnly value={pixData.qrCode} />
                <button
                  className="btn-copiar"
                  onClick={() => {
                    navigator.clipboard.writeText(pixData.qrCode);
                    toast.success("Código copiado!");
                  }}
                >
                  COPIAR CÓDIGO PIX
                </button>
              </div>

              <button
                className="btn-finalizar-fluxo"
                onClick={() => {
                  setShowPixModal(false);
                  navigate("/meu-perfil");
                }}
              >
                JÁ PAGUEI / IR PARA MEUS PEDIDOS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
